// Google Sign-In DIRECT côté WEB (hors Entra) via le flux OAuth par REDIRECTION
// (authorization code). Fiable partout (y compris navigation privée) — pas de
// One Tap/FedCM. Au clic : redirection vers Google ; au retour sur
// /auth/google/callback : on échange le code contre une session maison (backend).
// Résolu par webpack via l'extension .web.ts.

import { resolveApiBase, getEnvConfig } from '../config/env';
import { decodeJwt } from './entraConfig';
import type { Session } from './entraAuth.web';

const SESSION_KEY = 'ccjaw.session';
const STATE_KEY = 'ccjaw.google.state';
const CALLBACK_PATH = '/auth/google/callback';

function redirectUri(): string {
  return `${window.location.origin}${CALLBACK_PATH}`;
}

function randomState(): string {
  const a = new Uint8Array(24);
  crypto.getRandomValues(a);
  let s = '';
  for (let i = 0; i < a.length; i++) s += String.fromCharCode(a[i]);
  return btoa(s).replace(/[+/=]/g, '');
}

/** Lance la connexion Google : redirige vers Google (la page se recharge). */
export async function loginWithGoogle(): Promise<Session | null> {
  const state = randomState();
  sessionStorage.setItem(STATE_KEY, state);
  const params = new URLSearchParams({
    client_id: getEnvConfig().AUTH_GOOGLE_WEB_CLIENT_ID,
    redirect_uri: redirectUri(),
    response_type: 'code',
    scope: 'openid email profile',
    state,
    prompt: 'select_account',
    access_type: 'online',
  });
  window.location.assign(`https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`);
  return null; // la page redirige : aucune session retournée ici
}

/** Au retour de Google : échange le code -> session backend. Sinon no-op. */
export async function completeGoogleIfCallback(): Promise<Session | null> {
  if (typeof window === 'undefined' || window.location.pathname !== CALLBACK_PATH) return null;
  const qs = new URLSearchParams(window.location.search);
  const code = qs.get('code');
  const state = qs.get('state');
  const err = qs.get('error');
  const saved = sessionStorage.getItem(STATE_KEY);
  sessionStorage.removeItem(STATE_KEY);
  const cleanup = () => window.history.replaceState({}, document.title, '/');

  if (err) {
    cleanup();
    throw new Error(`Google: ${err}`);
  }
  if (!code) {
    cleanup();
    return null;
  }
  if (!state || state !== saved) {
    cleanup();
    throw new Error('invalid Google state');
  }
  // ⚠️ A NETWORK CUT IS NOT AN AUTHENTICATION REFUSAL, yet the screen said it the same way.
  // Measured: `ERR_NETWORK_CHANGED` (a Wi-Fi / VPN / sleep interface change) makes `fetch` fail
  // with `TypeError: Failed to fetch`, which propagated as-is — the user read "sign-in refused"
  // and went hunting for a Google account problem while the server answered perfectly. The two
  // causes call for opposite gestures: retry, or check your account.
  let res: Response;
  try {
    res = await fetch(`${resolveApiBase()}/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirect_uri: redirectUri() }),
      // ⚠️ WITHOUT THIS, THE SIGN-IN SUCCEEDS AND THE SESSION IS BORN DEAD. The API is a
      // different ORIGIN, so a cross-origin `fetch` without `credentials: 'include'` makes the
      // browser DISCARD the `Set-Cookie` of the response. The server parks the refresh coupon in
      // an HttpOnly cookie (CCT-320) and the browser throws it away, in silence: the access
      // token works, and the very first renewal finds nothing to renew.
      //
      // ⚠️ MEASURED ON DEV, in the server telemetry, three times in forty minutes:
      //     session_refresh refusé : DecodeError (coupon ABSENT, 0 car., source cookie)
      // The coupon was neither expired nor rejected — it had never been stored. Every Google
      // session therefore died at its first renewal, which is the "recurring sign-out".
      //
      // ⚠️ AND THE BODY NO LONGER CARRIES IT EITHER. `_park_session_coupon` REMOVES
      // `refresh_token` from the JSON, deliberately — leaving it would put a durable credential
      // back within reach of any script on the page. So the cookie is the only copy: dropping it
      // loses it for good.
      credentials: 'include',
    });
  } catch (e) {
    cleanup();
    const err = new Error('network') as Error & { cause?: unknown; kind?: string };
    err.kind = 'network';       // read by the screen to pick the message
    err.cause = e;
    throw err;
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    cleanup();
    throw new Error(`Connexion Google refusée: ${data?.detail || res.status}`);
  }
  const session: Session & { kind?: string } = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Math.floor(Date.now() / 1000) + (data.expires_in || 3600),
    claims: decodeJwt(data.access_token),
    kind: 'google',
  };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  cleanup();
  return session;
}
